import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { format, addDays, subDays } from 'date-fns'
import {
  ChevronLeft, ChevronRight, RefreshCw, Clock, Pencil
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { timeEntryService } from '@/services/timeEntryService'
import { toast } from 'sonner'

const HOUR_WIDTH = 100 // px per hour
const ROW_HEIGHT = 52  // px per user row
const TOTAL_HOURS = 24
const LABEL_WIDTH = 200 // left panel width for user info

const formatDurationFromSeconds = (seconds) => {
  if (!seconds) return '0m'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

const getUserName = (user) => {
  if (user?.employee?.first_name) return `${user.employee.first_name} ${user.employee.last_name}`
  return user?.email || 'Unknown'
}

const getInitial = (user) => {
  if (user?.employee?.first_name) return user.employee.first_name.charAt(0).toUpperCase()
  return user?.email?.charAt(0)?.toUpperCase() || '?'
}

const toHourOffset = (date, dayDate) => {
  const d = new Date(date)
  const dayStart = new Date(dayDate + 'T00:00:00')
  const diffMs = d - dayStart
  return Math.max(0, Math.min(TOTAL_HOURS, diffMs / (1000 * 60 * 60)))
}

const formatHourLabel = (hour) => {
  if (hour === 0) return '12 AM'
  if (hour < 12) return `${hour} AM`
  if (hour === 12) return '12 PM'
  return `${hour - 12} PM`
}

// Split a "yyyy-MM-ddTHH:mm" string into { date, time }
const splitDT = (dt) => dt
  ? { date: dt.slice(0, 10), time: dt.slice(11, 16) }
  : { date: '', time: '' }

// Merge date + time back into "yyyy-MM-ddTHH:mm"
const mergeDT = (date, time) => (date && time) ? `${date}T${time}` : ''

// Edit dialog for a time entry (admin use in TeamView)
const EditEntryDialog = ({ entry, open, onClose, onSaved }) => {
  const startDT = format(new Date(entry.start_time), "yyyy-MM-dd'T'HH:mm")
  const endDT   = entry.end_time ? format(new Date(entry.end_time), "yyyy-MM-dd'T'HH:mm") : ''

  const [start, setStart] = useState(splitDT(startDT))
  const [end,   setEnd]   = useState(splitDT(endDT))
  const [note,  setNote]  = useState(entry.note || '')
  const [loading, setLoading] = useState(false)

  const todayDate = format(new Date(), 'yyyy-MM-dd')
  const nowTime   = format(new Date(), 'HH:mm')

  const handleSubmit = async (e) => {
    e.preventDefault()
    const startISO = mergeDT(start.date, start.time)
    const endISO   = mergeDT(end.date, end.time)
    const now = new Date()

    if (!start.date || !start.time) return toast.error('Start date and time are required')
    if (new Date(startISO) > now)
      return toast.error('Start time cannot be in the future')

    if (!end.date || !end.time)
      return toast.error('End date and time are required')
    if (new Date(endISO) > now)
      return toast.error('End time cannot be in the future')
    if (new Date(endISO) <= new Date(startISO))
      return toast.error('End time must be after start time')

    setLoading(true)
    try {
      await timeEntryService.update(entry.id, {
        start_time: new Date(startISO).toISOString(),
        end_time: endISO ? new Date(endISO).toISOString() : undefined,
        note: note.trim() || null
      })
      toast.success('Time entry updated')
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update time entry')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            Edit Time Entry
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-1">
          <div className="text-xs text-muted-foreground">
            {entry.task?.title && <span className="font-medium">{entry.task.title}</span>}
            {entry.task?.project?.name && <span> · {entry.task.project.name}</span>}
          </div>

          {/* Start */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Start</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                value={start.date}
                max={todayDate}
                onChange={(e) => setStart(s => ({ ...s, date: e.target.value }))}
                className="h-9 text-sm"
              />
              <Input
                type="time"
                value={start.time}
                max={start.date === todayDate ? nowTime : undefined}
                onChange={(e) => setStart(s => ({ ...s, time: e.target.value }))}
                className="h-9 text-sm"
              />
            </div>
          </div>

          {/* End */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">End</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                value={end.date}
                max={todayDate}
                onChange={(e) => setEnd(s => ({ ...s, date: e.target.value }))}
                className="h-9 text-sm"
              />
              <Input
                type="time"
                value={end.time}
                max={end.date === todayDate ? nowTime : undefined}
                onChange={(e) => setEnd(s => ({ ...s, time: e.target.value }))}
                className="h-9 text-sm"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What were they working on?"
              rows={2}
              className="text-sm resize-none"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" size="sm" className="h-8 text-xs flex-1" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Horizontal time bar for a single entry
const TimeBar = ({ entry, dayDate, onEdit }) => {
  const startHour = toHourOffset(entry.start_time, dayDate)
  const endHour = entry.end_time
    ? toHourOffset(entry.end_time, dayDate)
    : toHourOffset(new Date(), dayDate)
  const isActive = !entry.end_time

  const left = startHour * HOUR_WIDTH
  const width = Math.max((endHour - startHour) * HOUR_WIDTH, 6)

  const projectColor = entry.task?.project?.color || '#3B82F6'
  const taskTitle = entry.task?.title || 'Unknown task'
  const projectName = entry.task?.project?.name || ''
  const duration = entry.duration
    ? formatDurationFromSeconds(entry.duration)
    : formatDurationFromSeconds(Math.floor((Date.now() - new Date(entry.start_time)) / 1000))

  const startTime = format(new Date(entry.start_time), 'h:mm a')
  const endTime = entry.end_time ? format(new Date(entry.end_time), 'h:mm a') : 'now'

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            onClick={() => onEdit?.(entry)}
            className={cn(
              'absolute top-1.5 bottom-1.5 rounded flex items-center px-1.5 gap-1 overflow-hidden cursor-pointer transition-opacity hover:opacity-80 hover:ring-2 hover:ring-white/60',
              isActive && 'animate-pulse'
            )}
            style={{
              left: `${left}px`,
              width: `${width}px`,
              backgroundColor: projectColor,
            }}
          >
            {width >= 60 && (
              <span className="text-[10px] font-medium truncate text-white">
                {taskTitle}
              </span>
            )}
            {width >= 130 && (
              <span className="text-[9px] text-white/70 shrink-0">{duration}</span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          <div className="space-y-1">
            <p className="font-medium text-sm">{taskTitle}</p>
            <p className="text-xs text-muted-foreground">{projectName}</p>
            <p className="text-xs">{startTime} - {endTime} ({duration})</p>
            {entry.note && <p className="text-xs italic">{entry.note}</p>}
            {isActive && <p className="text-xs text-green-600 font-medium">Currently tracking</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Now indicator — vertical red line
const NowIndicator = ({ dayDate }) => {
  const [now, setNow] = useState(new Date())
  const today = new Date().toISOString().split('T')[0]
  const isToday = dayDate === today

  useEffect(() => {
    if (!isToday) return
    const interval = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(interval)
  }, [isToday])

  if (!isToday) return null

  const offset = toHourOffset(now, dayDate)
  const left = offset * HOUR_WIDTH

  return (
    <div
      className="absolute top-0 bottom-0 z-10 pointer-events-none"
      style={{ left: `${left}px` }}
    >
      <div className="w-0.5 h-full bg-red-500 relative">
        <div className="absolute -top-1 -left-[3px] w-2 h-2 rounded-full bg-red-500" />
      </div>
    </div>
  )
}

// User row — left label + timeline lane
const UserRow = ({ member, dayDate, isLast, onEdit }) => {
  const name = getUserName(member.user)
  const initial = getInitial(member.user)
  const isActive = !!member.activeTimer
  const entries = member.entries || []
  const allBlocks = member.activeTimer ? [...entries, member.activeTimer] : entries

  return (
    <div className={cn('flex', !isLast && 'border-b border-border/40')}>
      {/* Fixed left label */}
      <div
        className="shrink-0 flex items-center gap-2.5 px-3 border-r border-border/40 bg-background sticky left-0 z-10"
        style={{ width: LABEL_WIDTH, height: ROW_HEIGHT }}
      >
        <div className="relative shrink-0">
          <Avatar className="h-7 w-7">
            <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
              {initial}
            </AvatarFallback>
          </Avatar>
          {isActive && (
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{name}</p>
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Clock className="h-2.5 w-2.5" />
            {formatDurationFromSeconds(member.totalSeconds)}
          </div>
        </div>
      </div>

      {/* Timeline lane */}
      <div
        className="relative"
        style={{ width: TOTAL_HOURS * HOUR_WIDTH, height: ROW_HEIGHT }}
      >
        {allBlocks.map((entry) => (
          <TimeBar key={entry.id} entry={entry} dayDate={dayDate} onEdit={onEdit} />
        ))}
      </div>
    </div>
  )
}

const TeamView = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [editingEntry, setEditingEntry] = useState(null)
  const scrollRef = useRef(null)
  const queryClient = useQueryClient()

  const { data, isLoading, refetch, isFetching } = useQuery(
    ['team-view', date],
    () => timeEntryService.getTeamView(date),
    { staleTime: 30000 }
  )

  const team = data?.data?.team || []

  const sortedTeam = useMemo(() => {
    return [...team].sort((a, b) => {
      if (a.activeTimer && !b.activeTimer) return -1
      if (!a.activeTimer && b.activeTimer) return 1
      return b.totalSeconds - a.totalSeconds
    })
  }, [team])

  const todayStr = new Date().toISOString().split('T')[0]
  const isToday = date === todayStr

  const goToday = () => setDate(todayStr)
  const goPrev = () => setDate(format(subDays(new Date(date + 'T12:00:00'), 1), 'yyyy-MM-dd'))
  const goNext = () => {
    const next = format(addDays(new Date(date + 'T12:00:00'), 1), 'yyyy-MM-dd')
    if (next <= todayStr) setDate(next)
  }

  const dateLabel = format(new Date(date + 'T12:00:00'), 'EEEE, MMMM d, yyyy')

  // Auto-scroll to ~8 AM horizontally on load
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 8 * HOUR_WIDTH - 40
    }
  }, [])

  const activeCount = sortedTeam.filter(m => m.activeTimer).length

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">Team View</h1>
          {activeCount > 0 && (
            <span className="text-xs font-medium bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400 px-2 py-0.5 rounded-full">
              {activeCount} active
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border rounded-lg">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <button
              onClick={goToday}
              className={cn(
                'text-sm px-3 py-1 font-medium transition-colors',
                isToday ? 'text-primary' : 'text-foreground hover:text-primary'
              )}
            >
              {isToday ? 'Today' : dateLabel}
            </button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goNext} disabled={isToday}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-1.5"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

        {isLoading ? (
          <div className="space-y-3 flex-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : sortedTeam.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            No team members found
          </div>
        ) : (
          <div className="flex-1 border rounded-lg overflow-hidden bg-background">
            <div ref={scrollRef} className="overflow-auto h-full">
              <div style={{ minWidth: LABEL_WIDTH + TOTAL_HOURS * HOUR_WIDTH }}>
                {/* Sticky hour header */}
                <div className="flex sticky top-0 z-20 bg-muted/50 border-b border-border/60 backdrop-blur-sm">
                  <div
                    className="shrink-0 border-r border-border/40 bg-muted/50 sticky left-0 z-30"
                    style={{ width: LABEL_WIDTH, height: 32 }}
                  />
                  <div className="flex relative" style={{ width: TOTAL_HOURS * HOUR_WIDTH }}>
                    {Array.from({ length: TOTAL_HOURS }).map((_, i) => (
                      <div
                        key={i}
                        className="border-r border-border/30 flex items-end pb-1 pl-1.5"
                        style={{ width: HOUR_WIDTH, height: 32 }}
                      >
                        <span className="text-[10px] text-muted-foreground">{formatHourLabel(i)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User rows with vertical grid + now indicator */}
                <div className="relative">
                  {/* Vertical hour grid lines */}
                  <div className="absolute pointer-events-none" style={{ left: LABEL_WIDTH, top: 0, bottom: 0, width: TOTAL_HOURS * HOUR_WIDTH }}>
                    {Array.from({ length: TOTAL_HOURS + 1 }).map((_, i) => (
                      <div
                        key={i}
                        className="absolute top-0 bottom-0 border-l border-border/20"
                        style={{ left: `${i * HOUR_WIDTH}px` }}
                      />
                    ))}
                    <NowIndicator dayDate={date} />
                  </div>

                  {/* Rows */}
                  {sortedTeam.map((member, idx) => (
                    <UserRow
                      key={member.user.id}
                      member={member}
                      dayDate={date}
                      isLast={idx === sortedTeam.length - 1}
                      onEdit={setEditingEntry}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

      {editingEntry && (
        <EditEntryDialog
          entry={editingEntry}
          open={!!editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => queryClient.invalidateQueries(['team-view', date])}
        />
      )}
    </div>
  )
}

export default TeamView
